import { useContext, useEffect, useMemo, useState } from 'react';
import { Group, Object3D } from 'three';

import { GeodataBoundaryPolygonModel } from '../../../../../typings/api/skymap/rest/v0/geodata';
import { isDefined } from '../../../utils/variables';
import { loadBoundaryPolygonGlb } from '../integrations/providers/drone-harmony/dialogs/edit-flight-plan-dialog/load-boundary-polygon-glb';
import { GeodataSpatialReferenceContext } from './gcp-page/geodata-spatial-reference-state';
import { useBoundaryPolygons } from './use-boundary-polygons';

export function useViewerBoundaryPolygons(geodataId: string) {
  const { origo } = useContext(GeodataSpatialReferenceContext);
  const { boundaryPolygons, isPending, errorList } = useBoundaryPolygons(geodataId);
  const [boundaryPolygonObjects, setBoundaryPolygonObjects] = useState<Object3D[]>([]);
  const [loadingStatus, setLoadingStatus] = useState<
    Record<string, 'idle' | 'loading' | 'loaded' | 'error'>
  >({});

  // Filter boundary polygons that have GLB data URLs
  const glbBoundaryPolygons = useMemo(() => {
    return boundaryPolygons.filter(
      (polygon: GeodataBoundaryPolygonModel) =>
        polygon.simDataUrl && polygon.enabled && polygon.status === 'COMPLETED',
    );
  }, [boundaryPolygons]);

  // Load GLB data for each boundary polygon
  useEffect(() => {
    const loadGlbData = async () => {
      const newObjects: Object3D[] = [];
      const newLoadingStatus: Record<string, 'idle' | 'loading' | 'loaded' | 'error'> = {};

      for (const polygon of glbBoundaryPolygons) {
        newLoadingStatus[polygon.id] = 'loading';

        const result = await loadBoundaryPolygonGlb(polygon);

        if (result.success) {
          // Calculate the difference between the GLB offset and the coordinate system origo
          if (isDefined(origo)) {
            const positionOffset = result.offset.clone().sub(origo);
            result.boundaryGroup.position.copy(positionOffset);
          }

          newObjects.push(result.boundaryGroup);
          newLoadingStatus[polygon.id] = 'loaded';
        } else {
          console.error(`Failed to load GLB for boundary polygon ${polygon.id}:`, result.error);
          newLoadingStatus[polygon.id] = 'error';
        }
      }

      setBoundaryPolygonObjects(newObjects);
      setLoadingStatus(newLoadingStatus);
    };

    if (glbBoundaryPolygons.length > 0) {
      void loadGlbData();
    } else {
      setBoundaryPolygonObjects([]);
      setLoadingStatus({});
    }
  }, [glbBoundaryPolygons, origo]);

  // Create a single Object3D group containing all boundary polygon objects
  const boundaryPolygonsGroup = useMemo(() => {
    const group = new Group();
    group.name = 'boundary-polygons';

    boundaryPolygonObjects.forEach((obj) => {
      group.add(obj);
    });

    return group;
  }, [boundaryPolygonObjects]);

  return {
    boundaryPolygonsGroup,
    boundaryPolygonObjects,
    glbBoundaryPolygons,
    loadingStatus,
    isPending,
    errorList,
  };
}
